import { InjectionToken } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { ReplaySubject } from 'rxjs';
import { resolveDeepLinkTarget } from './deep-link-target';
import { environment } from '../environments/environment';

/**
 * A Universal Link (iOS) / App Link (Android) can be delivered DURING launch — a
 * cold start where the OS opens the app straight onto the tapped URL — before
 * Angular has bootstrapped and any component listener has attached. Capacitor
 * always boots the WebView at its local root (never the deep path), so the URL
 * only ever arrives through this native channel, never Angular's initial
 * navigation. We therefore start listening from main.ts, BEFORE
 * bootstrapApplication, and buffer the URL in a ReplaySubject(1); AppComponent
 * still receives it once it subscribes a tick later.
 */
export const pendingDeepLink$ = new ReplaySubject<string>(1);

// Whether a link the app can open has arrived on either channel below, and when
// the launch URL is known. Together they answer "did a link open this launch?"
// (wasOpenedByLink). On the web both keep their defaults: no link, settled.
let linkReceived = false;
let launchUrlSettled: Promise<void> = Promise.resolve();

export function initDeepLinkCapture(): void {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  // Warm start / already-running: Android singleTask onNewIntent and iOS
  // continue-userActivity both surface here.
  App.addListener('appUrlOpen', (event) => noteLaunchLink(event.url));
  // Cold-start backstop (mainly custom-scheme launches; a duplicate for a
  // universal link is harmless — the consumer de-dupes).
  launchUrlSettled = App.getLaunchUrl()
    .then((result) => {
      if (result?.url) {
        noteLaunchLink(result.url);
      }
    })
    .catch(() => {
      /* no launch url / unsupported — nothing to route */
    });
}

/**
 * Hand a captured link to AppComponent, and count it as opening the app only when
 * AppComponent can route it (resolveDeepLinkTarget, the same test it applies). A
 * link it will ignore, such as a staging link reaching the production app, must not
 * hold back the auto-resume either. Exported for the spec.
 */
export function noteLaunchLink(url: string): void {
  if (resolveDeepLinkTarget(url, environment.clientUrl)) {
    linkReceived = true;
  }
  pendingDeepLink$.next(url);
}

/**
 * Did a link open this launch? Resolves once the launch URL has settled, so the
 * answer is known before anything decides on its own where the app should go.
 *
 * Home's auto-resume asks this first: a link names the event to open and must win
 * over the one saved from last time. A link that LAUNCHES the app reaches iOS as
 * appUrlOpen, which the App plugin retains until a listener attaches — the
 * listener above is the first bridge call — so it has landed by the time
 * getLaunchUrl() answers.
 */
export function wasOpenedByLink(): Promise<boolean> {
  return launchUrlSettled.then(() => linkReceived);
}

/** wasOpenedByLink, injectable so a spec can answer it. */
export const OPENED_BY_LINK = new InjectionToken<() => Promise<boolean>>('OPENED_BY_LINK', {
  providedIn: 'root',
  factory: () => wasOpenedByLink,
});
